import { Config } from "../model/Config.js";
import { VOGate } from "../model/DataStructure.js";
import { Language } from "../model/Language.js";
import { GateItem } from "../display/GateItem.js";
import { GateOverlay } from "../display/GateOverlay.js";
import { Assets } from "../model/Assets.js";

type GatePanel = "game" | "home";

export class Gate {

    public element: HTMLDivElement;

    private _overlay: GateOverlay;
    private _home: HTMLDivElement;
    private _game: HTMLDivElement;
    private _content: HTMLDivElement;
    private _control: HTMLDivElement;
    private _items: GateItem[] = [];
    private _itemContainer: HTMLDivElement;
    private _resetBar: HTMLDivElement;
    private _plane: HTMLDivElement;
    private _touchToStartMessage: HTMLDivElement;

    private _animateItem: GateItem = null;
    private _planeAnimation: gsap.core.Tween;
    private _endAnimationPlayed: boolean = false;

    constructor(gatePost: VOGate) {
        if (!gatePost) {
            console.warn("Gate could not be instantiated. Missing gates parameter.");
            return;
        }

        var gates = gatePost.entries || [];

        this.element = document.createElement("div");
        this.element.id = "gate";
        this.element.classList.add("scale-to-fit", "green-bg");
        this.element.setAttribute("data-panel", "home");
        document.body.appendChild(this.element);

        this._home = document.createElement("div");
        this._home.id = "gate-home";
        this._home.setAttribute("data-state", "intro");
        this.element.appendChild(this._home);

        var homeIntro = document.createElement("div");
        homeIntro.id = "gate-home-intro";
        this._home.appendChild(homeIntro);

        var homeIntroTitle = document.createElement("div");
        homeIntroTitle.id = "gate-home-intro-title";
        homeIntroTitle.innerHTML = "Green Gate Game";
        // Language.label(homeIntroTitle, "gate.title", "", "Green Gate Game");
        homeIntro.appendChild(homeIntroTitle);

        var homeIntroGerman = document.createElement("div");
        homeIntroGerman.id = "gate-home-intro-german";
        homeIntroGerman.classList.add("btn");
        Language.label(homeIntroGerman, "gate.language-de", "", "German");
        homeIntro.appendChild(homeIntroGerman);

        var homeIntroEnglish = document.createElement("div");
        homeIntroEnglish.id = "gate-home-intro-english";
        homeIntroEnglish.classList.add("btn");
        Language.label(homeIntroEnglish, "gate.language-en", "", "English");
        homeIntro.appendChild(homeIntroEnglish);

        this._game = document.createElement("div");
        this._game.id = "gate-game";
        this.element.appendChild(this._game);

        var gameTitle = document.createElement("div");
        gameTitle.id = "gate-game-title";
        Language.label(gameTitle, "gate.title", "", "Green Gate Game");
        this._game.appendChild(gameTitle);

        var lufthansLogo = document.createElement("div");
        lufthansLogo.id = "gate-lufthansa-logo";
        this.element.appendChild(lufthansLogo);

        this._content = document.createElement("div");
        this._content.id = "gate-content";
        this._content.style.backgroundImage = "url(" + gatePost.canvas.url + ")";
        this._game.appendChild(this._content);

        this._touchToStartMessage = document.createElement("div");
        this._touchToStartMessage.id = "gate-touch-message";
        Language.label(this._touchToStartMessage, "gate.touch-to-start", "", "Bitte Bildschirm antippen, um zu starten.");
        this._content.appendChild(this._touchToStartMessage);

        this._resetBar = document.createElement("div");
        this._resetBar.id = "gate-reset-bar";
        Language.label(this._resetBar, "gate.reset", "", "Neustarten");
        this._game.appendChild(this._resetBar);

        this._control = document.createElement("div");
        this._control.id = "gate-control";
        this._game.appendChild(this._control);

        this._itemContainer = document.createElement("div");
        this._itemContainer.id = "gate-item-container";
        this._game.appendChild(this._itemContainer);

        this._overlay = new GateOverlay(this);
        this._game.appendChild(this._overlay.element);

        //Language control.
        var showLanguageControl = false;
        if (showLanguageControl) {
            this.addLanguageControl();
        }

        //Horizontal margin left and right.
        var marginH: number = 40;
        var overallWidth: number = 0;
        for (var gate of gates) {
            overallWidth += gate.gate_image_default.width;
        }

        var containerWidth = this.element.offsetWidth;
        var availableWidth = containerWidth - 2 * marginH;
        var gapH: number = gates.length <= 1 ? 0 : e5.Calc.clamp((availableWidth - overallWidth) / (gates.length - 1), 10, 80);

        var width: number = gapH * (gates.length - 1) + overallWidth;
        var offX: number = (availableWidth - width) * 0.5 + marginH;
        for (var gate of gates) {
            var offY = (this._control.offsetHeight - gate.gate_image_default.height) * 0.5;
            offY += this._content.offsetHeight;
            var item = new GateItem(gate, this, offX, offY);
            offX += gapH + gate.gate_image_default.width;
            this._itemContainer.appendChild(item.content);
            this._itemContainer.appendChild(item.dropped);
            this._itemContainer.appendChild(item.control);
            this._itemContainer.appendChild(item.animate);
            this._items.push(item);
            item.onDragStart.add((item) => {
                this.onDragStartItem(item);
            });

            item.onMatchChanged.add((item, matched) => {
                this.onMatchedItem(item, matched);
            });

            item.onClickContent.add((item) => {
                this.onClickItem(item);
            });
        }

        this._plane = document.createElement("div");
        this._plane.id = "gate-plane";
        this._plane.style.backgroundImage = "url(" + Assets.getUrl("gate-plane") + ")";
        this._game.appendChild(this._plane);

        var planeLabel = document.createElement("div");
        planeLabel.id = "gate-plane-label";
        Language.label(planeLabel, "gate.plane-label");
        this._plane.appendChild(planeLabel);

        this._planeAnimation = gsap.to(this._plane, { duration: 7, x: 8000, ease: "none" });
        this._planeAnimation.pause();

        this._animateItem = this._items[Math.floor(this._items.length * 0.5)];

        this.element.addEventListener("pointerdown", () => {
            if (this.panel == "home" && this._home.getAttribute("data-state") == "start")
                this.panel = "game";
            else if (this.panel == "game") {
                this._touchToStartMessage.classList.remove("active");
                this._animateItem.running = false;
            }
        });

        this._resetBar.addEventListener("pointerdown", (e) => {
            e.stopImmediatePropagation();
            this.reset();
        });

        homeIntroGerman.addEventListener("pointerdown", (e) => {
            e.stopImmediatePropagation();
            Language.current = "de";
            this.panel = "game";
        });

        homeIntroEnglish.addEventListener("pointerdown", (e) => {
            e.stopImmediatePropagation();
            Language.current = "en";
            this.panel = "game";
        });

        this._overlay.onClose.add((userInitiated) => {
            if (this.finished && !this._endAnimationPlayed && userInitiated) {
                this._endAnimationPlayed = true;
                this._planeAnimation.restart();
                this._control.classList.remove("active");
                this._resetBar.classList.add("active");
            }
        });

        if (window.api) {
            window.api.lastInputInfo.maxIdleTime = Config.maxIdleTimeGate * 1000;
            window.api.lastInputInfo.addEventLisener("idle", () => this.reset());
        }
    }

    private addLanguageControl(): void {
        var languageControl = document.createElement("div");
        languageControl.id = "gate-language-control";
        // this.element.appendChild(languageControl);

        var btnDe = document.createElement("div");
        btnDe.className = "language-button";
        btnDe.setAttribute("data-lang", "de");
        btnDe.innerHTML = "DE";
        btnDe.classList.toggle("selected", Language.current == "de");
        btnDe.addEventListener("pointerdown", (e) => {
            e.stopImmediatePropagation();
            Language.current = "de";
        }, true);
        languageControl.appendChild(btnDe);

        var btnEn = document.createElement("div");
        btnEn.className = "language-button";
        btnEn.setAttribute("data-lang", "en");
        btnEn.innerHTML = "EN";
        btnEn.classList.toggle("selected", Language.current == "en");
        btnEn.addEventListener("pointerdown", (e) => {
            e.stopImmediatePropagation();
            Language.current = "en";
        }, true);
        languageControl.appendChild(btnEn);
    }

    private reset(): void {
        this.panel = "home";
    }

    private set panel(panel: GatePanel) {
        if (this.panel == panel)
            return;

        if (panel == "game") {
            for (var item of this._items) {
                item.setMatched(false, false);
            }
            this._endAnimationPlayed = false;
            this._planeAnimation.restart();
            this._planeAnimation.pause();
            this._resetBar.classList.remove("active");
            this._animateItem.running = true;
            this._touchToStartMessage.classList.add("active");
            this._home.setAttribute("data-state", "intro");
            this._control.classList.add("active");
        }
        else if (panel == "home") {
            this._animateItem.running = false;
            this._touchToStartMessage.classList.remove("active");
            this._overlay.close();
        }
        this.element.setAttribute("data-panel", panel);
    }

    private get panel(): GatePanel {
        return this.element.getAttribute("data-panel") as GatePanel;
    }

    private onClickItem(item: GateItem): void {
        this._overlay.open(item.data);
    }

    private onMatchedItem(item: GateItem, matched: boolean): void {
        if (matched) {
            this._overlay.open(item.data);
        }
    }

    protected get finished(): boolean {
        var matchedCount = 0;
        for (var item of this._items) {
            if (item.matched)
                matchedCount++;
        }
        return matchedCount == this._items.length;
    }

    private onDragStartItem(item: GateItem): void {
        this._overlay.close();
    }

    public get scale(): number {
        return parseFloat(this.element.getAttribute("data-scale"));
    }

    public get itemContainer(): HTMLDivElement {
        return this._itemContainer;
    }
}